import { useState, useEffect, useCallback } from 'react'

const STORAGE_KEY = 'attraflow-welcome-dismissed'

/**
 * Custom hook to manage the welcome dialog state
 * Tracks whether the user has dismissed the welcome dialog using localStorage
 *
 * @returns Object containing:
 *  - isFirstVisit: boolean indicating if this is the user's first visit
 *  - dismissWelcome: function to dismiss the welcome dialog
 *  - resetWelcome: function to reset the welcome state (useful for testing/debugging)
 */
export function useWelcomeDialog() {
  const [isFirstVisit, setIsFirstVisit] = useState<boolean>(() => {
    const dismissed = localStorage.getItem(STORAGE_KEY)
    return dismissed !== 'true'
  })

  // Sync with localStorage in case it changes in another tab
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setIsFirstVisit(e.newValue !== 'true')
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  const dismissWelcome = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, 'true')
    setIsFirstVisit(false)
  }, [])

  const resetWelcome = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY)
    setIsFirstVisit(true)
  }, [])

  return {
    isFirstVisit,
    dismissWelcome,
    resetWelcome,
  }
}
